import { api } from "./client";
import type {
  OrderDetail,
  OrderListRequest,
  OrderListResponse,
  PolicyPerson as PolicyholderDTO,
  BeneficiaryVO as BeneficiaryDTO,
  OrderFinancialVO as FinancialDataDTO,
  OrderProductPlanVO,
  OrderProductPlanUpdateDTO,
  OrderAttachmentConfig,
  OrderBasicDTO,
  CreateBasicOrderRequest,
  CreateBasicOrderResponse,
  OrderSignatureDTO,
  InsuredDTO,
  ComplianceAuditDTO,
  OrderStatusCountVO,
  OrderStatusCountResponse,
  OrderChangeVO,
  OrderChangeWebVO,
  OrderChangeHistoryWebRequest,
  ConfirmOrderChangeWebRequest,
  OrderCraDTO,
  SignatureConfigResponse,
  AuditOrderDTO,
  AmlAuditDTO,
  OrderAuditDetailVO,
  OrderProcessFlowRecordVO,
  OrderCra,
  AmlSearchResultVO,
  AmlSearchRecordVO,
  AmlCancelDTO,
  OrderFormPdfRequest,
  CraResultVO,
  PageOrderListVO,
  SmartOrderStatusCountVO,
  PolicyholderVO,
  SmartOrderBasicDTO,
  SmartOrderCreateResponse,
  AssignAdvisorDTO,
  AssignAdvisorRequest,
  SmartPolicyholderDTO,
  SmartOrderAppointmentDTO,
  OrderIntendedProductDTO,
  SmartInsuredDTO,
  SmartOrderFinancialDTO,
  HighPremiumOrder,
  PremiumCapacityRequest,
  PremiumCapacityResponse,
  OrderMaintenanceDTO,
  OrderPaymentRecord,
  OrderPaymentRecordVO,
  OrderPaymentRecordDTO,
  FileType,
  File,
  AuditRecordListRequest,
  AuditRecordVO,
  CreateAuditRecordRequest,
  DeleteAuditRecordRequest,
  CrossSellingConfirmationVO,
  CrossSellingRejectRequest,
  CrossSellingTransferRequest,
  TransferCustomerRequest,
} from "./types/order";
import type { FileUpload, OrderAttachmentDTO, PolicyAttachmentDTO } from "./types/common";
import type { RequestConfig } from "../request";

// 订单相关 API

/**
 * 获取订单详情
 * GET /api/v1/order/detail
 */
export function getOrderDetail<T = OrderDetail>(orderId: string | number) {
  return api.get<T>("/order/detail", { params: { orderId } });
}

/**
 * 获取 Smart 订单列表（分页）- 转介人端
 * GET /api/v1/smart/order/list
 * 使用 Spring Data 标准分页响应格式
 */
export function getSmartOrderList(params: OrderListRequest, config?: RequestConfig) {
  return api.get<PageOrderListVO>("/smart/order/list", { ...config, params });
}

export function updateOrder(orderId: number, referrerUid?: number): Promise<boolean> {
  return api.put<boolean>("/order/update-referrer", undefined, {
    params: { orderId, referrerUid },
  });
}

/**
 * 修改產品計劃
 * PUT /api/v1/order/product-plan
 */
export function updateOrderProductPlan(data: OrderProductPlanUpdateDTO): Promise<boolean> {
  return api.put<boolean>("/order/product-plan", data);
}

/**
 * 查询订单附件配置
 * GET /api/v1/order/attachment-config
 */
export function getAttachmentConfig(orderId: number): Promise<OrderAttachmentConfig[]> {
  return api.get<OrderAttachmentConfig[]>("/order/attachment-config", {
    params: { orderId },
  });
}

/**
 * 删除订单
 * PUT /api/v1/order/delete
 */
export function deleteOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/order/delete", undefined, { params: { orderId } });
}

/**
 * 上传文件
 * POST /api/v1/basic/upload-file
 */
export function uploadFile(data: FormData): Promise<string> {
  return api.post<string>("/basic/upload-file", data);
}

/**
 * 获取文件签名 URL（用于预览/下载）
 * GET /api/v1/basic/file-signed-url
 */
export function getFileSignedUrl(path: string): Promise<string> {
  return api.get<string>("/basic/file-signed-url", { params: { path } });
}

/**
 * 上传订单附件
 * POST /api/v1/order/upload-attachment
 */
export function uploadAttachment(data: OrderAttachmentDTO): Promise<boolean> {
  return api.post<boolean>("/order/upload-attachment", data);
}

/**
 * 删除订单附件
 * PUT /api/v1/order/delete-attachment
 * @param orderId 订单ID
 * @param attachmentId 附件ID
 */
export function deleteAttachment(orderId: number, attachmentId: number): Promise<boolean> {
  return api.put<boolean>("/order/delete-attachment", undefined, {
    params: { orderId, attachmentId },
  });
}

/**
 * 查询订单签署文件配置
 * GET /api/v1/order/signature-config
 * @param orderId 订单ID
 */
export function getSignatureConfig(orderId: number): Promise<SignatureConfigResponse> {
  return api.get<SignatureConfigResponse>("/order/signature-config", {
    params: { orderId },
  });
}

/**
 * 待签署-签署订单接口
 * PUT /api/v1/order/signed-order
 * @param orderId 订单ID
 */
export function signOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/order/signed-order", undefined, {
    params: { orderId },
  });
}

/**
 * 待确认-驳回订单
 * PUT /api/v1/order/rejected-order
 * @param orderId 订单ID
 */
export function rejectOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/order/rejected-order", undefined, {
    params: { orderId },
  });
}

/**
 * 订单审核-审核订单
 * PUT /api/v1/order/audit-order
 */
export function auditOrder(data: AuditOrderDTO): Promise<boolean> {
  return api.put<boolean>("/order/audit-order", data);
}

/**
 * 订单审核-AML审核
 * PUT /api/v1/order/aml-audit
 */
export function amlAudit(data: AmlAuditDTO): Promise<boolean> {
  return api.put<boolean>("/order/aml-audit", data);
}

/**
 * 查询订单审核详情
 * GET /api/v1/order/audit-detail
 * @param orderId 订单ID
 */
export function getOrderAuditDetail(orderId: number | string): Promise<OrderAuditDetailVO> {
  return api.get<OrderAuditDetailVO>("/order/audit-detail", {
    params: { orderId },
  });
}

/**
 * 订单审核-重新签署
 * PUT /api/v1/order/re-sign-order
 * @param orderId 订单ID
 */
export function reSignOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/order/re-sign-order", undefined, {
    params: { orderId },
  });
}

/**
 * 合规审核-审核订单
 * PUT /api/v1/order/compliance-audit
 */
export function complianceAudit(data: ComplianceAuditDTO): Promise<boolean> {
  return api.put<boolean>("/order/compliance-audit", data);
}

/**
 * 查阅批改内容
 * GET /api/v1/order/change-history
 * @param orderId 订单ID
 * @param flowRecordId 流程记录ID
 * @returns OrderChangeVO[]
 */
export function getOrderChangeHistory(orderId: number, flowRecordId: number): Promise<OrderChangeVO> {
  return api.get<OrderChangeVO>("/order/change-history", {
    params: { orderId, flowRecordId },
  });
}

/**
 * 查阅批改内容（Pending）
 * GET /api/v1/order/pending-change-history
 * @param orderId 订单ID
 * @param flowRecordId 流程记录ID
 * @returns OrderChangeVO | null
 */
export function getPendingOrderChangeHistory(orderId: number, flowRecordId: number): Promise<OrderChangeVO | null> {
  return api.get<OrderChangeVO | null>("/order/pending-change-history", {
    params: { orderId, flowRecordId },
  });
}

/**
 * 查阅批改内容（Pending 优先，空数据回退）
 * @param orderId 订单ID
 * @param flowRecordId 流程记录ID
 * @returns OrderChangeVO
 */
export async function fetchPreferPendingChangeHistory(orderId: number, flowRecordId: number): Promise<OrderChangeVO> {
  const pendingData = await getPendingOrderChangeHistory(orderId, flowRecordId);
  if (pendingData.changeDetails && pendingData.changeDetails.length > 0) {
    return pendingData;
  }

  return getOrderChangeHistory(orderId, flowRecordId);
}

/**
 * 查询客户风险评估问卷
 * GET /api/v1/order/cra-info
 * @param orderId 订单ID
 * @returns RiskEvaluationVO
 */
export function getRiskEvaluation(orderId: number): Promise<OrderCra> {
  return api.get<OrderCra>("/order/cra-info", { params: { orderId } });
}

/**
 * 客户风险评估问卷PDF下载
 * GET /api/v1/order/cra-file
 * @param orderId 订单ID
 * @returns PDF文件内容
 */
export function downloadRiskEvaluationPdf(orderId: number | string): Promise<{ blob: Blob; filename: string; save: () => void }> {
  return api.getBlob("/order/cra-file", { params: { orderId } });
}

/**
 * 查询订单审核记录
 * GET /api/v1/order/audit-order-record
 * @param orderId 订单ID
 * @returns OrderProcessFlowRecordVO
 */
export function getOrderAuditRecord(orderId: number): Promise<OrderProcessFlowRecordVO> {
  return api.get<OrderProcessFlowRecordVO>("/order/audit-order-record", {
    params: { orderId },
  });
}

/**
 * 取消订单
 * PUT /api/v1/order/cancel-order
 * @param orderId 订单ID
 */
export function cancelOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/order/cancel-order", undefined, {
    params: { orderId },
  });
}

/**
 * 查阅批改内容-WEB
 * GET /api/v1/smart/order/change-history
 * @param params OrderChangeHistoryWebRequest
 * @returns OrderChangeWebVO
 */
export function getOrderChangeHistoryWeb(params: OrderChangeHistoryWebRequest) {
  return api.get<OrderChangeWebVO | boolean>("/smart/order/pending-change-history", {
    params,
  });
}

/**
 * 获取 AML 结果记录
 * GET /api/v1/aml/individuals/record
 * @param orderId 订单ID
 * @returns AmlSearchRecordVO
 */
export function getAmlResultList(orderId: number | string): Promise<AmlSearchRecordVO> {
  return api.get<AmlSearchRecordVO>("/aml/individuals/record", {
    params: { orderId },
    timeout: 60000, // 60秒超时
  });
}

/**
 * 订单审核-客户确认修改
 * PUT /api/v1/order/audit-order-confirmation
 * @param orderId 订单ID
 * @param idCard 证件号（可选）
 * @param signature 客户确认修改签名
 */
export function confirmOrderChange(orderId: number, idCard?: string, signature?: string): Promise<boolean> {
  return api.put<boolean>("/order/audit-order-confirmation", {
    orderId,
    idCard,
    signature,
  });
}

/**
 * 订单审核-客户确认修改-WEB
 * PUT /api/v1/smart/order/audit-order-confirmation
 * @param params ConfirmOrderChangeWebRequest
 */
export function confirmOrderChangeWeb(params: ConfirmOrderChangeWebRequest): Promise<boolean> {
  return api.put<boolean>("/smart/order/audit-order-confirmation", params);
}

/**
 * AML 查询
 * GET /api/v1/aml/individuals/search
 * @param orderId 订单ID
 * @returns AmlSearchRecordVO
 */
export function searchAmlIndividuals(orderId: number | string): Promise<AmlSearchRecordVO> {
  return api.get<AmlSearchRecordVO>("/aml/individuals/search", {
    params: { orderId },
    timeout: 60000, // 60秒超时
  });
}

/**
 * 取消 AML 结果
 * PUT /api/v1/aml/individuals/cancel
 * @param data AmlCancelDTO
 * @returns boolean
 */
export function cancelAmlResult(data: AmlCancelDTO): Promise<boolean> {
  return api.put<boolean>("/aml/individuals/cancel", data, {
    timeout: 60000, // 60秒超时
  });
}

/**
 * 查看 AML 结果
 * GET /api/v1/aml/individuals/specific-profile-pdf
 * @param recordId 记录ID
 * @param resourceId 资源ID
 * @returns Blob 文件内容
 */
export function getAmlResultFile(recordId: number, resourceId: string): Promise<{ blob: Blob; filename: string; save: () => void }> {
  return api.getBlob("/aml/individuals/specific-profile-pdf", {
    params: { recordId, resourceId },
    timeout: 60000, // 60秒超时
  });
}

/**
 * 查看 AML 正常结果 PDF
 * GET /api/v1/aml/individuals/normal-pdf
 * @param recordId 记录ID
 * @returns Blob 文件内容
 */
export function getAmlNormalResultFile(recordId: number): Promise<{ blob: Blob; filename: string; save: () => void }> {
  return api.getBlob("/aml/individuals/normal-pdf", {
    params: { recordId },
    timeout: 60000, // 60秒超时
  });
}

/**
 * 订单表单 PDF 下载
 * GET /api/v1/order/form-pdf
 * @param params OrderFormPdfRequest
 * @returns Blob 文件内容
 */
export function downloadOrderFormPdf(params: OrderFormPdfRequest): Promise<{ blob: Blob; filename: string; save: () => void }> {
  return api.getBlob("/order/form-pdf", { params });
}

/**
 * 获取 CRA 风险评估结果
 * GET /api/v1/order/cra-result
 * @param orderId 订单ID
 * @returns CraResultVO 风险评估结果
 */
export function getCraResult(orderId: number | string): Promise<CraResultVO> {
  return api.get<CraResultVO>("/order/cra-result", {
    params: { orderId },
  });
}

/**
 * 根据答案数组计算 CRA 风险评估结果
 * POST /api/v1/order/cra-result-by-answer

 * @param answer 答案数组
 * @returns CraResultVO[] 风险评估结果数组
 */
export function getCraResultByAnswer(answer: FormData[]): Promise<CraResultVO[]> {
  return api.post<CraResultVO[]>("/order/cra-result-by-answer", answer);
}

/**
 * 根据类型查询订单附件
 * GET /api/v1/order/attachment
 * @param orderId 订单ID
 * @param type 附件类型
 * @returns OrderAttachmentVO[] 订单附件列表
 */
export function getOrderAttachments(orderId: number | string, type: FileType): Promise<File[]> {
  return api.get<File[]>("/order/attachment", {
    params: { orderId, type },
  });
}

/**
 * 查询各状态订单数量
 * GET /api/v1/order/count-by-status
 * @returns OrderStatusCountResponse[] 各状态订单数量列表
 */
export function getOrderCountByStatus(): Promise<OrderStatusCountResponse[]> {
  return api.get<OrderStatusCountResponse[]>("/order/count-by-status");
}

/**
 * 查询各状态订单数量
 * GET /api/v1/smart/order/count-by-status
 * @returns SmartOrderStatusCountVO[] 各状态订单数量列表
 */
export function getSmartOrderCountByStatus(): Promise<SmartOrderStatusCountVO[]> {
  return api.get<SmartOrderStatusCountVO[]>("/smart/order/count-by-status");
}

/**
 * 验证客户唯一性
 * GET /api/v1/smart/order/verify-customer-unique
 * @param idType 身份证明文件类型
 * @param idNumber 身份证明文件号码
 * @returns PolicyholderVO | null (唯一性通过返回 null，不通过返回客户详情)
 */
export function verifyCustomerUnique(idType: string, idNumber: string): Promise<PolicyholderVO | null> {
  return api.get<PolicyholderVO | null>("/smart/order/verify-customer-unique", {
    params: { idType, idNumber },
  });
}

/**
 * 创建/更新 Smart Order
 * POST /api/v1/smart/order/create
 * @param data 订单数据 (draft: true 保存草稿, draft: false 创建并提交)
 * @returns 订单ID
 */
export function createSmartOrder(data: SmartOrderBasicDTO): Promise<SmartOrderCreateResponse> {
  return api.post<SmartOrderCreateResponse>("/smart/order/create", data);
}

/**
 * 提交 Smart Order
 * PUT /api/v1/smart/order/submit
 * @param orderId 订单ID
 * @returns 是否成功
 */
export function submitSmartOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/smart/order/submit", undefined, {
    params: { orderId: orderId },
  });
}

/**
 * 確認預約（简化版）
 * PUT /api/v1/order/assign-signer
 * @param orderId 订单ID
 * @param signerId 簽單員用戶ID
 * @returns 是否成功
 * @deprecated 请使用 assignAdvisor 完整版接口
 */
export function assignOrderSigner(orderId: number, signerId: number): Promise<boolean> {
  return api.put<boolean>("/order/assign-signer", undefined, {
    params: { orderId, signerId },
  });
}

/**
 * 分配业务代表
 * PUT /api/v1/order/assign-advisor
 * @param data 分配业务代表请求数据
 * @returns 是否成功
 */
export function assignAdvisor(data: AssignAdvisorDTO | AssignAdvisorRequest): Promise<boolean> {
  return api.put<boolean>("/order/assign-advisor", data);
}

/**
 * Smart Order 更新投保人
 * PUT /api/v1/smart/order/update-policyholder
 * @param data SmartPolicyholderDTO 投保人数据
 * @returns 是否成功
 */
export function updateSmartPolicyholder(data: SmartPolicyholderDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-policyholder", data);
}

/**
 * Smart Order 更新预约信息
 * PUT /api/v1/smart/order/update-appointment
 * @param data SmartOrderAppointmentDTO 预约信息数据
 * @returns 是否成功
 */
export function updateSmartAppointment(data: SmartOrderAppointmentDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-appointment", data);
}

/**
 * Smart Order 更新意向产品
 * PUT /api/v1/smart/order/update-intended-product
 * @param data OrderIntendedProductDTO 意向产品数据
 * @returns 是否成功
 */
export function updateSmartIntendedProduct(data: OrderIntendedProductDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-intended-product", data);
}

/**
 * Smart Order 更新受保人
 * PUT /api/v1/smart/order/update-insured
 * @param data SmartInsuredDTO 受保人数据
 * @returns 是否成功
 */
export function updateSmartInsured(data: SmartInsuredDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-insured", data);
}

/**
 * Smart Order 更新第二投保人
 * PUT /api/v1/smart/order/update-second-policyholder
 * @param data SmartPolicyholderDTO 第二投保人数据
 * @returns 是否成功
 */
export function updateSmartSecondPolicyholder(data: SmartPolicyholderDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-second-policyholder", data);
}

/**
 * Smart Order 更新第二受保人
 * PUT /api/v1/smart/order/update-second-insured
 * @param data SmartInsuredDTO 第二受保人数据
 * @returns 是否成功
 */
export function updateSmartSecondInsured(data: SmartInsuredDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-second-insured", data);
}

/**
 * 更新受益人
 * PUT /api/v1/smart/order/update-beneficiary
 * @param data SmartBeneficiaryDTO 受益人数据
 * @returns 是否成功
 */
export interface SmartBeneficiaryDTO {
  orderId: number;
  beneficiaryId?: number;
  relationship?: string;
  basic?: {
    chineseSurname?: string;
    chineseGivenName?: string;
    englishSurname?: string;
    englishGivenName?: string;
    idType?: string;
    idNumber?: string;
    gender?: string;
    birthDate?: string;
  };
  percentage?: number;
  other?: {
    remark?: string;
  };
}

export function updateSmartBeneficiary(data: SmartBeneficiaryDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-beneficiary", data);
}

/**
 * 删除受益人
 * PUT /api/v1/smart/order/delete-beneficiary
 * @param orderId 订单ID
 * @param beneficiaryId 受益人ID
 * @returns 是否成功
 */
export function deleteSmartBeneficiary(orderId: number, beneficiaryId: number): Promise<boolean> {
  return api.put<boolean>("/smart/order/delete-beneficiary", undefined, {
    params: { orderId, beneficiaryId },
  });
}

/**
 * Smart Order 更新财务资料
 * PUT /api/v1/smart/order/update-financial
 * @param data SmartOrderFinancialDTO 财务资料数据
 * @returns 是否成功
 */
export function updateSmartFinancial(data: SmartOrderFinancialDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/update-financial", data);
}

// Smart Order 更新保險策略
/**
 * PUT /api/v1/smart/order/save-cra-insured-policy
 * @param data SmartInsuredPolicyDTO 保險策略数据
 * @returns 是否成功
 */
export function saveSmartInsuredPolicy(data: SmartInsuredPolicyDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/save-cra-insured-policy", data);
}

/**
 * 保存 Smart Order CRA 客户已投保保单
 * PUT /api/v1/smart/order/save-cra-insured-policy
 * @param data SmartInsuredPolicyDTO 保險策略数据
 * @returns 是否成功
 * @alias saveSmartInsuredPolicy
 */
export function saveSmartCraInsuredPolicy(data: SmartInsuredPolicyDTO): Promise<boolean> {
  return api.put<boolean>("/smart/order/save-cra-insured-policy", data);
}

/**
 * 查询客户风险评估问卷附件配置
 * GET /api/v1/order/cra-attachment-config
 * @param orderId 订单ID
 * @returns OrderAttachmentConfig[] 附件配置列表
 */
export function getCraAttachmentConfig(orderId: number | string): Promise<OrderAttachmentConfig[]> {
  return api.get<OrderAttachmentConfig[]>("/order/cra-attachment-config", {
    params: { orderId },
  });
}
/**
 * 获取订单列表（分页）
 * GET /api/v1/order/list
 * 使用 Spring Data 标准分页响应格式
 */
export function getAdminOrderList(params: OrderListRequest, config?: RequestConfig) {
  return api.get<PageOrderListVO>("/order/list", { ...config, params });
}

/**
 * 导出订单列表
 * GET /api/v1/order/export
 */
export function exportAdminOrderList(params: OrderListRequest, config?: RequestConfig): Promise<void> {
  return api.download("/order/export", { ...config, params });
}

/**
 * 计算保费承担能力
 * PUT /api/v1/order/premium-capacity
 * @param data PremiumCapacityRequest 包含订单ID和财务分析数据
 * @returns PremiumCapacityResponse 保费承担能力计算结果
 */
export function calculatePremiumCapacity(data: PremiumCapacityRequest): Promise<PremiumCapacityResponse> {
  return api.put<PremiumCapacityResponse>("/order/premium-capacity", data);
}

/**
 * 维护保单
 * PUT /api/v1/order/maintenance-policy
 * @param data OrderMaintenanceDTO 保单维护数据
 * @returns 是否成功
 */
export function maintenancePolicy(data: OrderMaintenanceDTO): Promise<boolean> {
  return api.put<boolean>("/order/maintenance-policy", data);
}

/**
 * 查询保司保单状态列表
 * GET /api/v1/order/insurer-policy-status-list
 */
export function getInsurerPolicyStatusList(companyCode?: string): Promise<string[]> {
  return api.get<string[]>("/order/insurer-policy-status-list", {
    params: {
      companyCode: companyCode || undefined,
    },
  });
}

/**
 * 获取保单列表（分页）
 * GET /api/v1/order/policy-list
 * 使用 Spring Data 标准分页响应格式
 */
export function getPolicyList(params: OrderListRequest, config?: RequestConfig) {
  return api.get<PageOrderListVO>("/order/policy-list", { ...config, params });
}

/**
 * 查询首期缴费记录
 * GET /api/v1/order/initial-payment-record
 * @param orderId 订单ID
 * @returns OrderPaymentRecord[] 首期缴费记录列表
 */
export function getInitialPaymentRecord(orderId: number | string): Promise<OrderPaymentRecord[]> {
  return api.get<OrderPaymentRecord[]>("/order/initial-payment-record", {
    params: { orderId },
  });
}

/**
 * 查询续期缴费记录
 * GET /api/v1/order/renewal-payment-record
 * @param orderId 订单ID
 * @returns OrderPaymentRecordVO[] 续期缴费记录列表
 */
export function getRenewalPaymentRecord(orderId: number | string): Promise<OrderPaymentRecordVO[]> {
  return api.get<OrderPaymentRecordVO[]>("/order/renewal-payment-record", {
    params: { orderId },
  });
}

/**
 * 添加/更新缴费记录
 * POST /api/v1/order/payment-record
 * @param data 缴费记录数据
 * @returns boolean 操作是否成功
 */
export function savePaymentRecord(data: OrderPaymentRecordDTO): Promise<boolean> {
  return api.post<boolean>("/order/payment-record", data);
}

/**
 * 删除缴费记录
 * PUT /api/v1/order/delete-payment-record
 * @param paymentRecordId 缴费记录ID
 * @returns boolean 操作是否成功
 */
export function deletePaymentRecord(paymentRecordId: number): Promise<boolean> {
  return api.put<boolean>("/order/delete-payment-record", undefined, {
    params: { paymentRecordId },
  });
}

/**
 * 上传保单附件
 * POST /api/v1/order/upload-policy-attachment
 * @param data 保单附件数据
 * @returns boolean 操作是否成功
 */
export function uploadPolicyAttachment(data: PolicyAttachmentDTO): Promise<boolean> {
  return api.post<boolean>("/order/upload-policy-attachment", data);
}

/**
 * 删除保单附件
 * PUT /api/v1/order/delete-policy-attachment
 * @param orderId 订单ID
 * @param attachmentId 附件ID
 * @returns boolean 操作是否成功
 */
export function deletePolicyAttachment(orderId: number, attachmentId: number): Promise<boolean> {
  return api.put<boolean>("/order/delete-policy-attachment", undefined, {
    params: { orderId, attachmentId },
  });
}

/**
 * 查詢審核記錄列表
 * GET /api/v1/order/audit-record/list
 * @param params 查詢參數
 * @returns 審核記錄列表
 */
export function getAuditRecordList(params: AuditRecordListRequest): Promise<AuditRecordVO[]> {
  return api.get<AuditRecordVO[]>("/order/audit-record/list", { params });
}

/**
 * 創建審核記錄
 * POST /api/v1/order/audit-record/create
 * @param data 審核記錄數據
 * @returns boolean 操作是否成功
 */
export function createAuditRecord(data: CreateAuditRecordRequest): Promise<boolean> {
  return api.post<boolean>("/order/audit-record/create", data);
}

/**
 * 刪除審核記錄
 * @param params 刪除參數
 * @returns boolean 操作是否成功
 */
export function deleteAuditRecord(params: DeleteAuditRecordRequest): Promise<boolean> {
  return api.delete<boolean>("/order/audit-record/delete", undefined, { params });
}

/**
 * 撤回訂單
 * POST /api/v1/order/withdraw
 */
export function withdrawOrder(data: { orderId: number; withdrawReason?: string }): Promise<boolean> {
  return api.post<boolean>("/order/withdraw", data);
}

/**
 * 獲取交叉銷售確認數據
 * GET /api/v1/order/cross-selling-confirmation
 * @param orderId 訂單ID
 * @returns CrossSellingConfirmationVO 交叉銷售確認數據
 */
/**
 * 獲取交叉銷售確認數據
 */
export function getCrossSellingConfirmation(params: { customerId?: number; orderId?: number }): Promise<CrossSellingConfirmationVO> {
  return api.get<CrossSellingConfirmationVO>("/order/cross-selling-confirmation", {
    params,
  });
}

/**
 * 禁止簽署（交叉銷售）
 * PUT /api/v1/order/cross-selling/reject
 * @param data 禁止簽署請求參數
 * @returns boolean 操作是否成功
 */
export function crossSellingReject(data: CrossSellingRejectRequest): Promise<boolean> {
  return api.put<boolean>("/order/cross-selling/reject", undefined, {
    params: data,
  });
}

/**
 * 移交訂單客戶（訂單狀態）
 * PUT /api/v1/order/cross-selling/transfer
 * @param data 移交客戶請求參數
 * @returns boolean 操作是否成功
 */
export function crossSellingTransfer(data: CrossSellingTransferRequest): Promise<boolean> {
  return api.put<boolean>("/order/cross-selling/transfer", undefined, {
    params: data,
  });
}

/**
 * 移交客戶（交叉銷售狀態）
 * PUT /api/v1/order/cross-selling/transfer-customer
 * @param data 移交客戶請求參數
 * @returns boolean 操作是否成功
 */
export function transferCustomer(data: TransferCustomerRequest): Promise<boolean> {
  return api.put<boolean>("/order/cross-selling/transfer-customer", undefined, {
    params: data,
  });
}
